/**
 * Error fallback component for React error boundaries.
 * Displays user-friendly error message with collapsible error details and retry functionality.
 */
interface ErrorFallbackProps {
  /** The error that was caught by the error boundary */
  error: unknown;
  /** Function to reset the error boundary and retry rendering */
  resetError: () => void;
}

/**
 * React error fallback component that renders when an error boundary catches an error.
 */
export function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  return (
    <div className='p-4 bg-red-50 border border-red-200 rounded-lg m-4'>
      <h2 className='text-lg font-semibold text-red-800 mb-2'>
        Something went wrong
      </h2>
      <p className='text-red-600 mb-4'>
        An unexpected error occurred. Please try refreshing the page or contact
        support.
      </p>
      <details className='mb-4'>
        <summary className='cursor-pointer text-red-700'>Error details</summary>
        <pre className='mt-2 text-xs bg-red-100 p-2 rounded overflow-auto'>
          {error?.toString()}
        </pre>
      </details>
      <button
        onClick={resetError}
        className='bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700'
      >
        Try again
      </button>
    </div>
  );
}
